/** Tiny typed DOM helpers — the parts of jQuery this site actually used. */

/** Query all matching elements as a real array. */
export function all<T extends Element = HTMLElement>(
  selector: string,
  root: ParentNode = document,
): T[] {
  return Array.from(root.querySelectorAll<T>(selector));
}

/** Query a single element, or null. */
export function one<T extends Element = HTMLElement>(
  selector: string,
  root: ParentNode = document,
): T | null {
  return root.querySelector<T>(selector);
}

/**
 * Delegated event listener.
 *
 * Binds once on `root` instead of once per element, so markup added later still
 * works and there is nothing to re-bind. `handler` receives the matched element.
 */
export function delegate<K extends keyof HTMLElementEventMap>(
  root: Document | HTMLElement,
  type: K,
  selector: string,
  handler: (el: HTMLElement, event: HTMLElementEventMap[K]) => void,
): void {
  root.addEventListener(type, (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const match = target.closest<HTMLElement>(selector);
    if (match && root.contains(match)) handler(match, event as HTMLElementEventMap[K]);
  });
}

/** Run once the DOM is parsed (or immediately if it already is). */
export function ready(fn: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}

/** True when the visitor has asked for reduced motion. */
export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
